import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import t from '@i18n/t';
import { RootState } from '@state/types';
import { settingsActions } from '@state/settings';
import {
  darkBackground,
  darkBackgrounds,
  SoftBackground,
  softBackground,
  softBackgrounds
} from '@utils/theme/softBackground';
import { useThemeMode } from '@utils/theme/themeMode';
import MaterialIcon from '@components/common/icons/MaterialIcon';

type Mode = 'light' | 'dark';

type RowProps = {
  label: string;
  mode: Mode;
  /** A theme was just picked here while the app is in the other mode: offer to switch, to see it. */
  offerSwitch: boolean;
  items: SoftBackground[];
  picked: string;
  onPick: (key: string) => void;
  onSwitch: (mode: Mode) => void;
};

const SwatchRow: FC<RowProps> = ({ label, mode, offerSwitch, items, picked, onPick, onSwitch }) => (
  <Column>
    <RowHeader>
      <RowLabel>{label}</RowLabel>
      {/* Beside the mode that isn't on, once a theme is picked there, a way to see it (Petr, 2026-10-11): "In use now" on
          the other row read as the picked one being in use; under the swatch it sat too far from what it's about; and
          shown all the time it was noise. */}
      {offerSwitch && (
        <SwitchPill type="button" onClick={() => onSwitch(mode)}>
          <MaterialIcon name={mode === 'dark' ? 'dark_mode' : 'light_mode'} size={14} outline color="currentColor" />
          {mode === 'dark' ? t.SWITCH_TO_DARK_TO_SEE : t.SWITCH_TO_LIGHT_TO_SEE}
        </SwitchPill>
      )}
    </RowHeader>
    <Swatches role="radiogroup" aria-label={label}>
      {items.map(item => (
        <Swatch
          key={item.key}
          type="button"
          role="radio"
          aria-checked={item.key === picked}
          selected={item.key === picked}
          onClick={() => item.key !== picked && onPick(item.key)}
        >
          <Preview style={{ background: item.backgroundDark }}>
            <Card style={{ background: item.background }} />
          </Preview>
          <Label>{item.label}</Label>
        </Swatch>
      ))}
    </Swatches>
  </Column>
);

/**
 * The backgrounds behind the app (Petr, 2026-10-10: "so it's not always pure white"), one for light mode and one for
 * dark, so Auto and set hours can go from one to the other. Saved to the account, as the colour scheme is, so every
 * browser shows them; the app redraws in them at once.
 */
const BackgroundPicker: FC = () => {
  const dispatch = useDispatch();
  const light = useSelector((state: RootState) => softBackground(state.settings.userSettings?.background).key);
  const dark = useSelector((state: RootState) => darkBackground(state.settings.userSettings?.darkBackground).key);
  const { theme, setPreference } = useThemeMode();
  // The mode a theme was just picked for while the app is in the other one; gone once the mode changes, by the pill
  // or any other way.
  const [pickedWhileOff, setPickedWhileOff] = useState<Mode | null>(null);
  useEffect(() => setPickedWhileOff(null), [theme]);

  const pick = (mode: Mode, item: SoftBackground) => {
    dispatch(
      settingsActions.saveUserSettings.request(
        mode === 'light' ? { background: item.key, softWhiteSwitched: true } : { darkBackground: item.key }
      )
    );
    if (mode !== theme) setPickedWhileOff(mode);
  };

  return (
    <Rows>
      <SwatchRow
        label={t.BACKGROUND_LIGHT_MODE}
        mode="light"
        offerSwitch={pickedWhileOff === 'light' && theme !== 'light'}
        items={softBackgrounds}
        picked={light}
        onPick={key => pick('light', softBackground(key))}
        onSwitch={setPreference}
      />
      <SwatchRow
        label={t.BACKGROUND_DARK_MODE}
        mode="dark"
        offerSwitch={pickedWhileOff === 'dark' && theme !== 'dark'}
        items={darkBackgrounds}
        picked={dark}
        onPick={key => pick('dark', darkBackground(key))}
        onSwitch={setPreference}
      />
    </Rows>
  );
};

export default BackgroundPicker;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Swatches = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

// A soft tint of the accent, deepening as it is pointed at (soft hovers).
const SwitchPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border: none;
  border-radius: 999px;
  white-space: nowrap;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ theme }) => theme.primary};
  background: ${({ theme }) => `color-mix(in srgb, ${theme.primary} 12%, transparent)`};
  &:hover {
    background: ${({ theme }) => `color-mix(in srgb, ${theme.primary} 20%, transparent)`};
  }
  i {
    cursor: pointer;
  }
`;

const Swatch = styled.button<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border: none;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    background: ${({ theme }) => `color-mix(in srgb, ${theme.primary} 8%, transparent)`};
  }
  > div:first-child {
    box-shadow: 0 0 0 ${({ selected }) => (selected ? 2 : 1)}px
      ${({ selected, theme }) => (selected ? theme.primary : theme.borderColor)};
  }
`;

// A panel with a card on it, in the background's own two shades, as the app will look.
const Preview = styled.div`
  width: 64px;
  height: 44px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
`;

const Card = styled.div`
  position: absolute;
  left: 10px;
  top: 10px;
  right: 10px;
  bottom: 0;
  border-radius: 5px 5px 0 0;
  box-shadow: 0 0 3px rgba(63, 78, 97, 0.15);
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.textColor};
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const RowLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.textColor};
`;
